<template lang="pug">
div
    .p-a.white.lt.box-shadow
        .row
            .col-md-3.col-sm-4.m-y-sm.pull-right
                dynamic-filter-picker(
                    title="Custom Filter"
                )
                    div(slot="body")
                        override-filter

            .col-md-9.col-sm-4.m-y-sm
                a(:href='indexUrl') Station Pricebook
                span.text-muted  / {{ station_name }} ({{ pricebook.base_pricebook.name }})

    .padding
        .row
            v-loading.text-center.text-lg(loader="fetch-override-pricebook")

            .col-md-12.m-b-sm(v-if="!onLoading('fetch-override-pricebook')")
                .box
                    import-override(v-if="showImportModal" :pricebookId="id")
                    // existing-override component cause heavy query
                    //existing-override(:pricebook-id="id")
                    template(v-if="pricebook.products_count > 0")
                        v-server-table(
                            :url="url",
                            :columns="columns",
                            :options="options",
                            :name="tableName",
                            ref="datatable"
                        )
                            template(slot="beforeTable")
                                datatable-loader(:name="tableName")

                            template(slot="afterFilter", v-if="canManageStationPrice || canManagePricebook")
                                .box-tool
                                    ul.nav
                                        li.nav-item.inline.dropdown
                                            a.nav-link.text-muted.p-x-xs(
                                                data-toggle="dropdown"
                                                aria-expanded="false"
                                            )
                                                i.fa.fa-ellipsis-v
                                            .dropdown-menu.dropdown-menu-scale.pull-right
                                                a.dropdown-item(v-for="a in actionButtons" @click="a.action") {{ a.title }}

                            template(slot="status", slot-scope="props")
                                i.text-muted(v-if="!getEffectiveVisibility(props.row)") Inactive
                                i(v-else) Active

                        include aside-modal-override

                        edit-override(ref="editOverride", @entry-saved="fetchData")
                        add-products-form(v-if="showBatchUpdateModal" ref="addProductsForm", :pricebookId="id", @entry-saved="fetchData")
                        shelf-labels(v-if="showShelfLabelModal" ref="shelfLabels")
                    template(v-else)
                        setup-wizard(
                            v-if="canManageStationPricebook"
                            :pricebook-id="id"
                            :show-import="showImportForm"
                            :show-existing="showExistingForm"
                        )
</template>

<script>
const TableName = 'VUETABLE_PRICEBOOK_OVERRIDE';
const STORE_KEY = 'override_pricebook';

import Auth from '@js/plugins/auth';
import { mapActions, mapGetters, mapState } from 'vuex';
import ImportOverride from './import/import-override';
import pricebookModule from '@js/store/modules/override_pricebook';
import EditOverride from './edit_override.vue';
import AddProductsForm from './add-products-form.vue';
import SetupWizard from './batch/setup-wizard';
import { getEffectiveVisibility } from './helpers';
import DatepickerInput from '@js/components/datepicker/datepicker.vue';
import OverrideFilter from './override-filter';
import existingOverride from './existing-override';
import PermissionMixin from './override-permission';
import dynamicFilterPicker from '@js/components/filter/dynamic-filter-picker';
import dateFormat from '@js/mixins/date-format';
import Currency from '@js/plugins/currency';
import shelfLabels from './shelf-labels/shelf-labels-index.vue';

export default {
    components: {
        EditOverride,
        SetupWizard,
        AddProductsForm,
        ImportOverride,
        DatepickerInput,
        OverrideFilter,
        existingOverride,
        dynamicFilterPicker,
        shelfLabels,
    },

    mixins: [PermissionMixin, dateFormat],

    props: {
        id: {
            type: Number,
            required: true,
        },
    },

    data() {
        const canManagePricebook = Auth.hasPermissionTo(
            'franchise_manage_station_pricebook',
        );

        const canManageStationPrice = Auth.hasPermissionTo(
            'franchise_manage_station_price',
        );

        const franchiseCurrency = Currency.hasCurrency();

        let actionButtons = [
            {
                title: 'Batch Add',
                action: () => {
                    this.showBatchUpdateModal = true;
                    this.$nextTick(() => {
                        this.$refs.addProductsForm.$emit('show', 0);
                    });
                },
            },
        ];

        if (!canManagePricebook && !canManageStationPrice) {
            actionButtons = [];
        }

        if (canManagePricebook) {
            actionButtons = actionButtons.concat([
                {
                    title: 'Import',
                    action: () => {
                        this.showImportForm();
                    },
                },
                {
                    title: 'Batch Update',
                    action: () => {
                        this.showBatchUpdateModal = true;
                        this.$nextTick(() => {
                            this.$refs.addProductsForm.$emit('show', 1);
                        });
                    },
                },
                {
                    title: 'Edit Station Pricebook',
                    action: () => {
                        this.$refs.override_modal.$emit('show');
                    },
                },
                {
                    title: 'Shelf Labels',
                    action: () => {
                        this.showShelfLabelModal = true;
                        this.$nextTick(() => {
                            this.$refs.shelfLabels.$emit('show');
                        });
                    },
                },
            ]);
        }

        return {
            canManagePricebook,
            canManageStationPrice,
            editRowId: null,
            tableName: TableName,
            columns: [
                'code',
                'name',
                'pricebook_price',
                'pricing',
                'latest_cost',
                'status',
                'entry_product_created_at',
                'entry_product_updated_at',
            ],
            options: {
                checkbox: canManageStationPrice,
                checkbox_actions: {
                    api_delete: Laroute.route(
                        'franchise.inventory.override_pricebook.entries.delete',
                        { id: this.id },
                    ),
                    delete_title: 'Delete Product Price',
                },
                checkboxDisableCallback: row => row.pricebook_entry_id === null,
                texts: {
                    description: 'Customize product pricing and visibility',
                },
                rowClassCallback: () => 'pointer',
                rowClickCallback: row => {
                    this.$refs.editOverride.$emit('show', row);
                },
                headings: {
                    pricebook_price: `RRP (${
                        this.$store.getters.currency != null
                            ? this.$store.getters.currency
                            : franchiseCurrency
                    })`,
                    pricing: `Station Price (${
                        this.$store.getters.currency != null
                            ? this.$store.getters.currency
                            : franchiseCurrency
                    })`,
                    latest_cost: `Latest Cost (${
                        this.$store.getters.currency != null
                            ? this.$store.getters.currency
                            : franchiseCurrency
                    })`,
                    code: 'Barcode',
                    name: 'Product Name',
                    entry_product_created_at: 'Created At',
                    entry_product_updated_at: 'Modified At',
                },
                columnsClasses: {
                    pricebook_price: 'text-right',
                    pricing: 'text-right',
                    latest_cost: 'text-right',
                },
                templates: {
                    entry_product_created_at: (h, row) => {
                        if (row.entry_product_created_at) {
                            return this.dateFormat(
                                row.entry_product_created_at,
                            );
                        }
                    },
                    entry_product_updated_at: (h, row) => {
                        if (row.entry_product_updated_at) {
                            return this.dateFormat(
                                row.entry_product_updated_at,
                            );
                        }
                    },
                    pricing: (h, row) => {
                        return row.entry_price;
                    },
                },
                sortable: [
                    'entry_product_created_at',
                    'entry_product_updated_at',
                    'code',
                    'name',
                    'status',
                ],
                orderBy: {
                    ascending: false,
                    column: 'entry_product_created_at',
                },
            },
            actionButtons,
            showBatchUpdateModal: false,
            showShelfLabelModal: false,
            showImportModal: false,
        };
    },

    computed: {
        ...mapGetters(STORE_KEY, ['pricebook', 'pricebookToOverride']),
        ...mapState(STORE_KEY, ['filters']),

        start_at() {
            if (this.pricebook) {
                return moment(this.pricebook.start_at)
                    .format('DD MMM YYYY')
                    .toUpperCase();
            }

            return null;
        },

        station_name() {
            if (this.pricebook.outlets) {
                const station = this.pricebook.outlets[0];
                return station.name;
            }

            return null;
        },

        url() {
            return Laroute.route(
                'franchise.inventory.override_pricebook.entries',
                {
                    id: this.id,
                },
            );
        },

        indexUrl() {
            return Laroute.route(
                'franchise.inventory.override_pricebook.index',
            );
        },

        canManageStationPricebook() {
            return Auth.hasPermissionTo('franchise_manage_station_pricebook');
        },
    },

    watch: {
        filters: function(to, from) {
            if (to === from) return;
            this.setFilter();
        },
    },

    beforeCreate() {
        this.$store.registerModule(STORE_KEY, pricebookModule);
    },

    created() {
        this.setPricebookId(this.id);
        this.getOverridePricebook(this.id);
    },

    methods: {
        ...mapActions(STORE_KEY, [
            'setPricebookId',
            'getOverridePricebook',
            'updateOverridePricebook',
            'setOverridePricebookAttr',
        ]),

        setFilter() {
            this.$store.commit(`${this.tableName}/SET_CUSTOM_FILTER`, {
                filter: 'filters',
                value: this.filters,
            });
        },

        fetchData() {
            this.$refs.datatable.getData();
        },

        getEffectivePrice(entry) {
            if (entry.pricebook_restrict_price_override) {
                return entry.pricebook_price || entry.price;
            }
            return entry.override_price || entry.pricebook_price || entry.price;
        },

        getEffectiveVisibility(entry) {
            return getEffectiveVisibility(entry);
        },

        showImportForm() {
            this.showImportModal = true;
            this.$nextTick(() => {
                $('#import-override').modal('show');
            });
        },

        showExistingForm() {
            $('#existing-override').modal('show');
        },

        async submitOverride() {
            if (!(await this.$validator.validateAll())) return;

            try {
                await this.updateOverridePricebook();
                this.showToastrSuccess('Pricebook has been successfully saved');
                this.$refs.override_modal.$emit('hide');
            } catch (e) {
                this.showApiError(e);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
label {
    font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/override_pricebook/override-editor.vue